// must be first import because it side-effectively (real word) applies fixes
import "./fixes";

import OtelService from "./telemetry/OtelService";

import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";

import BasicSuspenseLoading from "./components/BasicSuspenseLoading";
import PreventIncognito from "./components/PreventIncognito";
import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { RecoilRoot } from "recoil";
import { useAuth } from "./state/auth";
import { getEnvFromLocal, useEnv } from "./state/env";
import { ToasterProvider } from "./v2/components/toaster";
import { LaunchDarklyContextProvider } from "./components/LaunchDarkly";
import { NoFrameExtensionProvider } from "./components/noframe/NoFrameExtension";
import { lazyRetry } from "./etc/lazyRetry";
import TestboxQueryClientProvider from "./etc/queryClient";
import "./index.css";
import { ConfirmationProvider } from "./v2/components/confirmation-dialogs";

function OtelUserSync() {
    const { data } = useAuth();
    const env = useEnv();
    useEffect(() => {
        if (env?.FRONTEND_OTEL_ENDPOINT) {
            OtelService.getInstance().initialize({
                endpoint: env.FRONTEND_OTEL_ENDPOINT,
                headers: env.FRONTEND_OTEL_HEADERS,
                deploymentEnvironment: env.DEPLOYMENT_ENVIRONMENT,
            });
        }
        OtelService.getInstance().setUser({
            userId: data?.user_id,
            email: data?.email,
            companyId: data?.company?.company_id,
            companyName: data?.company_name,
            workspaceId: data?.current_workspace?.workspace_id,
            djangoSessionId: data?.session_id ?? undefined,
        });
    }, [
        data?.user_id,
        data?.email,
        data?.company?.company_id,
        data?.company_name,
        data?.current_workspace?.workspace_id,
        data?.session_id,
        env?.FRONTEND_OTEL_ENDPOINT,
    ]);
    return null;
}

const App = lazyRetry(() => import("./App"));

// Initialize OTEL synchronously from cached /env so it's live before React mounts.
// Without this, the bootstrap fetches and the first navigation have no tracer and
// SigNoz shows the trace as "Missing span". Cold-cache first-ever visits still init
// later via OtelUserSync's effect; the singleton guard makes that call a no-op here.
const cachedEnv = getEnvFromLocal();
if (cachedEnv?.FRONTEND_OTEL_ENDPOINT) {
    OtelService.getInstance().initialize({
        endpoint: cachedEnv.FRONTEND_OTEL_ENDPOINT,
        headers: cachedEnv.FRONTEND_OTEL_HEADERS,
        deploymentEnvironment: cachedEnv.DEPLOYMENT_ENVIRONMENT,
    });
}

// Why is the NODE_ENV of staging environment "production"?  Shouldn't it be "stage" or "staging"?
if (process.env.NODE_ENV === "production") {
    Sentry.init({
        dsn: "https://604be4db1bda41d9b76b4f62aafcc0a1@o541645.ingest.sentry.io/5691638",
        integrations: [new Integrations.BrowserTracing()],

        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 1.0,
        environment: window.location.href.includes("app.testbox.com")
            ? "production"
            : "staging",
    });
}

const container = document.getElementById("root");
if (!container) {
    throw new Error('Root element with id="root" not found');
}

createRoot(container).render(
    <React.StrictMode>
        <PreventIncognito>
            <RecoilRoot>
                <OtelUserSync />
                <React.Suspense fallback={<BasicSuspenseLoading />}>
                    <TestboxQueryClientProvider>
                        <LaunchDarklyContextProvider>
                            <NoFrameExtensionProvider>
                                <ToasterProvider>
                                    <ConfirmationProvider>
                                        <App />
                                    </ConfirmationProvider>
                                </ToasterProvider>
                            </NoFrameExtensionProvider>
                        </LaunchDarklyContextProvider>
                    </TestboxQueryClientProvider>
                </React.Suspense>
            </RecoilRoot>
        </PreventIncognito>
    </React.StrictMode>
);
